/* eslint-disable camelcase */
import { combineReducers } from 'redux';
import { combineActions, handleActions } from 'redux-actions';
import {
  appContainerSettingFetchInitialized,
  appContainerSettingFetchSucceeded,
  appContainerSettingFetchFailed,
  appContainerSettingUpdateSucceeded,
  appContainerSettingsFetchInitialized,
  appContainerSettingsFetchSucceeded,
  appContainerSettingsFetchFailed,
} from '../ContainerSettings';

export const SETTING_NAMES = {
  fpc: 'fpc',
  containerJsPath: 'container_js_path',
  staticResourcesDomain: 'static_resources_domain',
  trackingDomain: 'tracking_domain',
  trackingScriptPath: 'tracking_script_path',
  trackingEndpointPath: 'tracking_endpoint_path',
  uiApisDomain: 'ui_apis_domain',
};

type DomainSettingValue = string | { is_enabled: boolean }
export type DomainSetting = 'fpc' |
'secure_cookies' |
'container_js_path' |
'static_resources_domain' |
'tracking_domain' |
'tracking_script_path' |
'tracking_endpoint_path' |
'ui_apis_domain' |
'spa_tracking' |
'automatic_scroll_tracking'

export interface DomainSettingsAttributes {
  value: DomainSettingValue
  is_custom?: boolean
}

export type SingleSettingResponse = {
  data?: {
    type: string
    id: string
    attributes: DomainSettingsAttributes
  }
}

export type MultipleSettingsResponse = {
  data?: {
    type: string
    id: string
    attributes: DomainSettingsAttributes
  }[]
}

export interface SettingState {
  isLoading: boolean
  failed: boolean
  is_custom?: boolean
  value: DomainSettingValue
}
const createInitialState = (value: DomainSettingValue): SettingState => ({
  isLoading: false,
  failed: false,
  value,
  is_custom: false,
});

type SettingStateAndPayload = SettingState & SingleSettingResponse;

const createReducer = (propertyName: string, initialState: SettingState) =>
  handleActions<SettingStateAndPayload>(
    {
      [combineActions(appContainerSettingFetchInitialized(propertyName),
        appContainerSettingsFetchInitialized).toString()]:
        (state: SettingState) => ({
          ...state,
          isLoading: true,
          failed: false,
        }),
      [combineActions(appContainerSettingFetchSucceeded(propertyName),
        appContainerSettingsFetchSucceeded).toString()]:
        (state: SettingState, { payload }:
        { payload}) => {
          if (Array.isArray(payload.data)) {
            const attributes = payload.data.find(
              ({ id }) => id === propertyName,
            )?.attributes;

            return {
              ...state,
              isLoading: false,
              value: attributes!.value,
              is_custom: attributes!.is_custom,
            };
          }

          return {
            ...state,
            isLoading: false,
            value: payload.data!.attributes.value,
            is_custom: payload.data!.attributes.is_custom,
          };
        },
      [combineActions(appContainerSettingFetchFailed(propertyName),
        appContainerSettingsFetchFailed).toString()]:
        (state: SettingState) => ({
          ...state,
          isLoading: false,
          failed: true,
        }),
      [appContainerSettingUpdateSucceeded(propertyName).toString()]:
        (state: SettingState, { payload }) => ({
          ...state,
          value: payload.data!.attributes.value,
          is_custom: payload.data!.attributes.is_custom,
        }),
    },
    initialState,
  );

const settingsReducer = combineReducers({
  fpc: createReducer(
    SETTING_NAMES.fpc,
    createInitialState({ is_enabled: false }),
  ),
  containerJsPath: createReducer(
    SETTING_NAMES.containerJsPath,
    createInitialState(''),
  ),
  staticResourcesDomain: createReducer(
    SETTING_NAMES.staticResourcesDomain,
    createInitialState(''),
  ),
  trackingDomain: createReducer(
    SETTING_NAMES.trackingDomain,
    createInitialState(''),
  ),
  trackingScriptPath: createReducer(
    SETTING_NAMES.trackingScriptPath,
    createInitialState(''),
  ),
  trackingEndpointPath: createReducer(
    SETTING_NAMES.trackingEndpointPath,
    createInitialState(''),
  ),
  uiApisDomain: createReducer(
    SETTING_NAMES.uiApisDomain,
    createInitialState(''),
  ),
});

export default settingsReducer;
